// Global OAuth callback deep-link listener.
//
// When the user signs in with Google on a Capacitor build, we open the
// OAuth URL in the system browser (Browser.open). After the user
// completes authentication, Supabase redirects to
// `com.sportsbio.app://auth-callback?code=...`. Android's intent
// filter on MainActivity routes that URL back into the app as an
// `appUrlOpen` event. This hook is the listener: it pulls the `code`
// out of the URL, exchanges it for a session via Supabase JS, and
// closes the system browser sheet.
//
// Mounted once globally inside AppShell so the listener is active no
// matter which screen is on top when the user finishes Google sign-in
// (typically AuthScreen, but the user might background the app and
// land somewhere else).

import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/lib/supabase";

/** Hostname we expect inside the custom scheme. Matches the
 *  android:host attribute on the AndroidManifest intent filter. */
const OAUTH_HOST = "auth-callback";

export function useOAuthDeepLink() {
  useEffect(() => {
    // The listener is only useful on native — on web, OAuth redirects
    // land on a normal URL and detectSessionInUrl handles them.
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    void (async () => {
      const handle = await CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
        try {
          // Custom-scheme URLs parse fine with the standard URL class
          // in modern WebView runtimes — Capacitor's WebView supports
          // it. The host check guards against other deep links that
          // might arrive on the same app (none today, but cheap to be
          // explicit about which event we handle).
          const parsed = new URL(url);
          if (parsed.host !== OAUTH_HOST) return;

          const code = parsed.searchParams.get("code");
          if (!code) {
            // No code in the URL — could be an OAuth error redirect
            // (e.g. user denied). Supabase returns ?error= in that
            // case. Surface as a quiet no-op so the AuthScreen's
            // existing useEffect doesn't navigate the user to a
            // half-applied destination.
            // eslint-disable-next-line no-console
            console.warn("[oauth] callback URL had no code:", url);
            await Browser.close().catch(() => { /* already closed */ });
            return;
          }

          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            // eslint-disable-next-line no-console
            console.error("[oauth] exchangeCodeForSession failed", error);
          }
          // After the session lands, supabase auth state change fires
          // and AuthContext picks up the new user. AuthScreen's
          // already-mounted useEffect sees `user` and routes via
          // resolveDestinationForUser.
          await Browser.close().catch(() => { /* already closed */ });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[oauth] deep link handler crashed", err);
          await Browser.close().catch(() => { /* already closed */ });
        }
      });
      listenerHandle = handle;
    })();

    return () => {
      void listenerHandle?.remove();
    };
  }, []);
}
